import { Box, useTheme } from "@mui/material"
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import StatusFeedback, { type FeedbackStatus } from "../components/status-feedback"
import type { MetodoPagamento, TrilhaPagamento } from "./payment-methods"
import type { FasePagamento } from "./use-pagamento-tef"

const IMG = '/images/totem-checkout'

const STATUS_POR_FASE: Record<FasePagamento, FeedbackStatus> = {
    processando: 'loading',
    aprovado: 'sucesso',
    erro: 'x',
}

export const MS_EXIBICAO_APROVADO: Record<TrilhaPagamento, number> = {
    cartao: 2600,
    pix: 5200,
}

interface PaymentProcessingProps {
    metodo: MetodoPagamento
    fase: FasePagamento
    valorFormatado: string
    mensagemErro?: string | null
    avisoRegistro?: string | null
    contaNaoFechou?: boolean
    registrando?: boolean
    ehQuitacao?: boolean
    saldoFormatado: string
    onConcluir: () => void
    onProximaPessoa?: () => void
    onEncerrar?: () => void
    onVoltarSelecao: () => void
}

export default function PaymentProcessing({ metodo, fase, valorFormatado, mensagemErro, avisoRegistro, contaNaoFechou = false, registrando = false, ehQuitacao = false, saldoFormatado, onConcluir, onProximaPessoa, onEncerrar, onVoltarSelecao }: PaymentProcessingProps): JSX.Element {
    const theme = useTheme()
    const cartaoSvg = theme.palette.mode === 'dark' ? `${IMG}/credit-card-payment-white.svg` : `${IMG}/credit-card-payment-black.svg`
    const ehCartao = metodo.trilha === 'cartao'
    const ehPix = metodo.trilha === 'pix'
    const temAviso = fase === 'aprovado' && !!avisoRegistro
    // Registrado e sem fechar (pagou a mais, conta levada ao caixa) não é divisão em andamento: só resta concluir.
    const contaEmAberto = fase === 'aprovado' && !ehQuitacao && !contaNaoFechou
    const podeAvancar = contaEmAberto && !!onProximaPessoa && !registrando
    const textoBotao = fase === 'erro' ? 'Escolher outra forma' : podeAvancar ? 'Próxima pessoa' : temAviso ? 'Concluir' : undefined
    const acaoBotao = fase === 'erro' ? onVoltarSelecao : podeAvancar ? onProximaPessoa : temAviso ? onConcluir : undefined
    const icone = podeAvancar ? <ArrowForwardRoundedIcon /> : temAviso ? <CheckRoundedIcon /> : undefined

    const acaoSecundaria = podeAvancar && onEncerrar
        ? { rotulo: 'Já paguei, encerrar', aoClicar: onEncerrar }
        : undefined

    return (
        <StatusFeedback
            status={temAviso ? 'alerta' : STATUS_POR_FASE[fase]}
            imagem={ilustracao()}
            titulo={titulo()}
            detalhe={detalhe()}
            textoBotao={textoBotao}
            onDismiss={acaoBotao}
            autoRetornoMs={fase === 'erro' ? 8000 : null}
            iconeBotao={icone}
            acaoSecundaria={acaoSecundaria}
            embutido
        />
    )

    function ilustracao(): JSX.Element | undefined {
        if (ehCartao && fase === 'processando') {
            return <Box component="img" src={cartaoSvg} alt="" sx={{ width: '100%', maxWidth: 380, objectFit: 'contain' }} />
        }

        if (ehPix && fase === 'processando') {
            return <Box component="img" src={`${IMG}/scan-qr-code.svg`} alt="" sx={{ maxHeight: 320, maxWidth: '100%', objectFit: 'contain' }} />
        }

        if (ehPix && fase === 'aprovado' && !avisoRegistro) {
            return <Box key="pix-pago" component="img" src={`${IMG}/scan-qr-code-approved-payment.svg`} alt="" sx={{ maxHeight: 320, maxWidth: '100%', objectFit: 'contain' }} />
        }

        return undefined
    }

    function titulo(): string {
        if (fase === 'erro') return 'Pagamento não concluído'

        if (fase === 'aprovado') {
            if (temAviso) {
                return contaNaoFechou
                    ? 'Pago, mas a conta não fechou — chame o atendente'
                    : 'Pago, mas não registrado — chame o atendente'
            }

            return 'Pagamento aprovado!'
        }

        return ehPix ? 'Pague com PIX' : `Pague com ${metodo.rotulo}`
    }

    function detalhe(): string {
        if (fase === 'erro') return mensagemErro ?? 'Não foi possível concluir o pagamento. Tente novamente ou escolha outra forma.'

        if (fase === 'aprovado') {
            if (temAviso) {
                return contaEmAberto
                    ? `${avisoRegistro} ${textoQueFalta()} — toque para continuar a divisão.`
                    : (avisoRegistro as string)
            }

            if (contaEmAberto) return `${textoQueFalta()} nesta conta`

            return `${metodo.rotulo} · ${valorFormatado}`
        }

        return ehPix
            ? `Escaneie o QR Code na maquininha com o app do seu banco · ${valorFormatado}`
            : `Aproxime, insira ou passe o cartão na maquininha · ${valorFormatado}`
    }

    function textoQueFalta(): string {
        return `Faltam ${saldoFormatado}`
    }
}
